import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { PUBLIC_ROUTES } from '@/components/seo/publicRoutes';
import { readConsent, subscribeConsent } from '@/lib/cookieConsent';
import './playbook-popup.css';

/**
 * L'INVITATION À 30 SECONDES : Pharma AI-Ready Brands H2 2026.
 *
 * Classement semestriel des 50 marques pharma (H1 2026 = Omnichannel Pharma
 * Guide, H2 2026 = US Top 50). Le formulaire poste sur le même Formspree que la
 * page /resources/pharma-geo-playbook-2026 (`xjgodzkd`), avec
 * `source_placement=popup_30s` pour distinguer l'entrée dans les leads. Le mail
 * de suivi (remerciement, code bêta, Calendly) part de la campagne inbound.
 *
 * RÈGLES. Pages publiques seulement (PUBLIC_ROUTES), jamais sur les documents
 * légaux ni sur la page du rapport elle-même. Jamais avant que le bandeau
 * cookies soit tranché, pour ne pas empiler deux couches. Une fois tous les
 * 14 jours au plus, plus jamais après un téléchargement.
 */
const FORM_ENDPOINT = 'https://formspree.io/f/xjgodzkd';
const PDF_FULL = '/playbook/pharma-ai-ready-brands-h2-2026-us-top50.pdf';
const COVER = '/playbook/playbook-2026-us-cover.jpg';
const KEY = 'aikka_h2_2026_popup';
const DELAY_MS = 30_000;
const SNOOZE_MS = 14 * 24 * 3600 * 1000;
const EXCLUDED = /^\/resources\/(legal|privacy|terms|cookies|security|pharma-geo-playbook-2026)/;

type State = 'idle' | 'sending' | 'done' | 'error';

function eligible(pathname: string): boolean {
  if (EXCLUDED.test(pathname)) return false;
  if (!PUBLIC_ROUTES.some((r) => r.path === pathname)) return false;
  try {
    const last = Number(window.localStorage.getItem(KEY) || 0);
    return Date.now() - last > SNOOZE_MS;
  } catch {
    return false;
  }
}

function remember(forever = false) {
  try {
    window.localStorage.setItem(KEY, String(forever ? Date.now() + 3650 * 24 * 3600 * 1000 : Date.now()));
  } catch {
    /* storage blocked */
  }
}

export function PlaybookPopup() {
  const { t } = useTranslation('pages');
  const { pathname } = useLocation();
  const [consented, setConsented] = useState<boolean>(() => readConsent() !== null);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<State>('idle');

  useEffect(() => subscribeConsent((c) => setConsented(c !== null)), []);

  useEffect(() => {
    if (!consented || open || !eligible(pathname)) return undefined;
    const timer = window.setTimeout(() => {
      const active = document.activeElement;
      if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
      if (!eligible(window.location.pathname)) return;
      setOpen(true);
      remember();
    }, DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [consented, pathname, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('sending');
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: new FormData(event.currentTarget),
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(String(response.status));
      setState('done');
      remember(true);
      const link = document.createElement('a');
      link.href = PDF_FULL;
      link.download = 'pharma-ai-ready-brands-h2-2026-us-top50.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setState('error');
    }
  }

  return (
    <div
      className="pb-popup-back"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pb-popup-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="pb-popup-card">
        <button type="button" className="pb-popup-close" aria-label={t('playbook2026.popup.close')} onClick={() => setOpen(false)}>
          <X size={16} aria-hidden />
        </button>
        <div className="pb-popup-media">
          <img className="pb-popup-cover" src={COVER} alt={t('playbook2026.coverAlt')} width={850} height={1100} />
        </div>
        <div className="pb-popup-body">
          {state === 'done' ? (
            <div role="status">
              <h2 id="pb-popup-title">{t('playbook2026.popup.doneTitle')}</h2>
              <p>{t('playbook2026.popup.doneBody')}</p>
              <a className="playbook-submit" href={PDF_FULL} download>
                {t('playbook2026.success.cta')}
              </a>
            </div>
          ) : (
            <>
              <p className="pb-popup-kicker">{t('playbook2026.popup.kicker')}</p>
              <h2 id="pb-popup-title">{t('playbook2026.popup.title')}</h2>
              <p>{t('playbook2026.popup.body')}</p>
              <form onSubmit={onSubmit}>
                <input type="hidden" name="_subject" value="Pharma AI-Ready Brands H2 2026 download (aikka.ai popup)" />
                <input type="hidden" name="form_type" value="playbook_2026_us_download" />
                <input type="hidden" name="resource" value="Pharma AI-Ready Brands H2 2026 US Top 50" />
                <input type="hidden" name="source_site" value="aikka.ai" />
                <input type="hidden" name="source_placement" value="popup_30s" />
                <input type="hidden" name="source_page" value={pathname} />
                <input name="name" required autoComplete="name" placeholder={t('playbook2026.popup.name')} aria-label={t('playbook2026.popup.name')} />
                <input type="email" name="email" required autoComplete="email" placeholder={t('playbook2026.form.email')} aria-label={t('playbook2026.form.email')} />
                <input name="company" required autoComplete="organization" placeholder={t('playbook2026.form.company')} aria-label={t('playbook2026.form.company')} />
                <label className="pb-popup-consent">
                  <input type="checkbox" name="consent" required />
                  <span>
                    {t('playbook2026.form.consent')} <a href="/resources/privacy">{t('playbook2026.form.privacy')}</a>
                  </span>
                </label>
                <button className="playbook-submit" type="submit" disabled={state === 'sending'}>
                  {state === 'sending' ? t('playbook2026.form.sending') : t('playbook2026.popup.submit')}
                </button>
                {state === 'error' && <p className="pb-popup-error">{t('playbook2026.form.error')}</p>}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
